import Modal from '../../../components/UI/Modals/Modal';
import classes from './EditSampleBarcodeModal.module.css';
import {
  selectSamplesTakenEditingStatus,
  resetEditingStatus,
  editSampleTaken,
} from '../../../store/slices/samplesTaken';
import { fetchSalesOrders, fetchServicesTranslations, SalesOrderInfo, selectServicesTranslations } from '../../../store/slices/salesOrders';
import { DiagnosticResult, fetchDiagnosticResults } from '../../../store/slices/diagnosticResults';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { client } from '../../../networking/client';

import {
  IServiceWithBiomarkerInfo,
  selectServicesWithBiomarkerInfos,
} from '../../../store/slices/services';
import { buildTranslatedServicesMap, getServiceBiomarker, getTranslatedServicesName, isBiomarkerValueValid } from '../../../utils/miscUtils';
import CustomTextField from '../../../components/UI/Inputs/CustomTextField';
import PrimaryButtonV2 from '../../../components/UI/Buttons/PrimaryButtonV2';
import Spinner from '../../../components/UI/Spinner/Spinner';
import CustomDropdown from '../../../components/UI/Inputs/CustomDropdown';
import {
  IAccountInfo,
  IPerson,
  selectAccountInfo,
  selectPersonLoggedIn,
  selectPersonsInLocation,
} from '../../../store/slices/auth';
import { useTranslation } from '../../../hooks/useTranslations';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onScanClick: () => void;
  scanValue: string;
  onChangeScanValue: (e: any) => void;
  sampleTakenId: number;
  selectedSalesOrder?: SalesOrderInfo;
};

const EditSampleBarcodeModal: React.FC<Props> = ({
  isOpen,
  onClose,
  selectedSalesOrder,
  sampleTakenId,
  scanValue,
  onChangeScanValue,
  onScanClick,
}) => {
  const dispatch = useAppDispatch();
  const { translations } = useTranslation();
  const sampleTakenUpdateStatus = useAppSelector<string>(selectSamplesTakenEditingStatus);
  const servicesWithBiomarkerInfo = useAppSelector<IServiceWithBiomarkerInfo[]>(
    selectServicesWithBiomarkerInfos
  );
  const [isEditingSample, setIsEditingSample] = useState<boolean>(false);
  const [salesOrder, setSalesOrder] = useState<SalesOrderInfo>();
  const [isAddingResults, setIsAddingResults] = useState<boolean>(false);
  const personsInLocation = useAppSelector<IPerson[]>(selectPersonsInLocation);
  const [personIdSelected, setPersonIdSelected] = useState<number>();
  const accountInfo = useAppSelector<IAccountInfo>(selectAccountInfo);
  const [showConfirmVoidModal, setShowConfirmVoidModal] = useState<boolean>(false);
  const [shouldDisableCTValueForPCR, setShouldDisableCTValueForPCR] = useState<boolean>(false);
  const personLoggedIn = useAppSelector<IPerson | undefined>(selectPersonLoggedIn);
    const servicesTranslations = useAppSelector(selectServicesTranslations);
    const lang = (localStorage.getItem('lang') || 'en').toLowerCase().trim();

  useEffect(() => {
    if (personsInLocation?.length > 0 && personsInLocation[0].person_id) {
      setPersonIdSelected(parseInt(personsInLocation[0].person_id));
    }
  }, [personsInLocation]);

  useEffect(() => {
    if (sampleTakenUpdateStatus === 'pending') {
      setIsEditingSample(true);
    } else if (sampleTakenUpdateStatus === 'fulfilled') {
      dispatch(fetchSalesOrders());
      dispatch(fetchDiagnosticResults());
      dispatch(resetEditingStatus());
      setIsEditingSample(false);
    } else if (sampleTakenUpdateStatus === 'rejected') {
      toast.error(translations('sample_barcode_already_exists').toString(), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      dispatch(resetEditingStatus());
      setIsEditingSample(false);
    }
  }, [sampleTakenUpdateStatus, dispatch]);

  useEffect(() => {
    setSalesOrder(selectedSalesOrder);
  }, [selectedSalesOrder]);

  const addResultHandler = async () => {
    if (salesOrder?.diagnostic_results && salesOrder.diagnostic_results.length > 0) {
      try {
        let valuesLeftToAttach = salesOrder.diagnostic_results.length;
        let positiveNegative: string | undefined;
        setIsAddingResults(true);
        for (const diagnosticResult of salesOrder?.diagnostic_results) {
          if (diagnosticResult.biomarker_value_result) {
            const serviceWithBiomarker = getServiceBiomarker(
              servicesWithBiomarkerInfo,
              diagnosticResult.biomarker_id,
              diagnosticResult.service_id
            );
            if (
              !isBiomarkerValueValid(
                serviceWithBiomarker?.minValue,
                serviceWithBiomarker?.maxValue,
                diagnosticResult.biomarker_value_result
              )
            ) {
              throw new Error(
                `${diagnosticResult.biomarker_name} ${translations('error.biomarker')}`
              );
            }
          }
          await client.put(`/healthpoint/diagnostic-result/${diagnosticResult.sample_taken_id}`, {
            biomarkerValueResults: shouldDisableCTValueForPCR
              ? 0
              : diagnosticResult.biomarker_value_result,
            biomarkerTextResult: shouldDisableCTValueForPCR
              ? 'Void'
              : diagnosticResult.biomarker_text_result,
            biomarkerId: diagnosticResult.biomarker_id,
          });
          if (diagnosticResult.biomarker_value_result || diagnosticResult.biomarker_text_result) {
            valuesLeftToAttach -= 1;
          }
          if (diagnosticResult.biomarker_text_result) {
            positiveNegative = diagnosticResult.biomarker_text_result;
          }
        }
        toast.success(translations('placeholder.successfully.updated'), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        dispatch(fetchSalesOrders());
        dispatch(fetchDiagnosticResults());
        if (valuesLeftToAttach === 0) {
          if (salesOrder.service.service_name.includes('PCR')) {
            console.log(salesOrder.diagnostic_results);
            await client.put(
              `/healthpoint/update-status-diagnostic/${accountInfo.account_number}`,
              {
                merchantPersonId: personLoggedIn?.person_external_id,
                workOrderId: salesOrder.work_order_service_diagnostic?.work_order_id,
                workOrderStatusId: positiveNegative?.toLowerCase() === 'void' ? 3 : 2,
                positiveNegative: positiveNegative,
                ctValue: shouldDisableCTValueForPCR
                  ? 0
                  : salesOrder.diagnostic_results[1].biomarker_value_result?.toString(),
                taId: salesOrder.sales_order.ta_id,
                transactionId: salesOrder.sales_order.transaction_id,
                salesOrderId: salesOrder.sales_order.sales_order_id,
                salesOrderExternalId: salesOrder.sales_order.sales_order_external_id,
                externalSalesOrderItemId: salesOrder.sales_order_item?.external_sales_order_item_id,
                personEmail: salesOrder.customer_person.person_email,
                personAccountNumber: salesOrder.customer_person.person_account_number,
                testId: salesOrder.diagnostic_results[0].sample_barcode,
                validatedByPersonId: personIdSelected,
              }
            );
          } else {
            await client.put(
              `/healthpoint/update-status-diagnostic/${accountInfo.account_number}`,
              {
                merchantPersonId: personLoggedIn?.person_external_id,
                workOrderId: salesOrder.work_order_service_diagnostic?.work_order_id,
                workOrderStatusId: 2,
                positiveNegative: positiveNegative,
                details: {
                  diagnose_results: salesOrder.diagnostic_results
                    .filter((d) => d.biomarker_value_result)
                    .map((diagnosticResult) => {
                      return {
                        property_id: parseInt(diagnosticResult.biomarker_external_id.toString()),
                        property_name: diagnosticResult.biomarker_name,
                        property_value: diagnosticResult.biomarker_value_result?.toString(),
                        machine_id: diagnosticResult.diagnostic_machine_external_id,
                      };
                    }),
                },
                taId: salesOrder.sales_order.ta_id,
                transactionId: salesOrder.sales_order.transaction_id,
                salesOrderId: salesOrder.sales_order.sales_order_id,
                salesOrderExternalId: salesOrder.sales_order.sales_order_external_id,
                externalSalesOrderItemId: salesOrder.sales_order_item?.external_sales_order_item_id,
                personEmail: salesOrder.customer_person.person_email,
                personAccountNumber: salesOrder.customer_person.person_account_number,
                testId:
                  salesOrder.product_group.product_group_id != 6
                    ? salesOrder.diagnostic_results[0].sample_barcode
                    : undefined,
                validatedByPersonId: personIdSelected,
              }
            );
          }
          // check if the other order items have results (closed or void)

          toast.success(translations('successfully.status').toString(), {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
          dispatch(fetchSalesOrders());
          dispatch(fetchDiagnosticResults());
          onClose();
        }
      } catch (err: any) {
        console.log('err', err);
        toast.error(err?.response?.data?.error ?? translations('error.result').toString(), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      } finally {
        setIsAddingResults(false);
      }
    }
  };

  const markAsVoidHandler = async () => {
    if (salesOrder?.diagnostic_results && salesOrder.diagnostic_results.length > 0) {
      try {
        setIsAddingResults(true);
        await client.put(`/healthpoint/update-status-diagnostic/${accountInfo.account_number}`, {
          merchantPersonId: personLoggedIn?.person_external_id,
          workOrderId: salesOrder.work_order_service_diagnostic?.work_order_id,
          workOrderStatusId: 3,
          positiveNegative: 'void',
          taId: salesOrder.sales_order.ta_id,
          transactionId: salesOrder.sales_order.transaction_id,
          salesOrderId: salesOrder.sales_order.sales_order_id,
          salesOrderExternalId: salesOrder.sales_order.sales_order_external_id,
          externalSalesOrderItemId: salesOrder.sales_order_item?.external_sales_order_item_id,
          personEmail: salesOrder.customer_person.person_email,
          personAccountNumber: salesOrder.customer_person.person_account_number,
          testId:
            salesOrder.product_group.product_group_id != 6
              ? salesOrder.diagnostic_results[0].sample_barcode
              : undefined,
          validatedByPersonId: personIdSelected,
        });
        dispatch(fetchSalesOrders());
        dispatch(fetchDiagnosticResults());
        toast.success(translations('success').toString(), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        onClose();
      } catch (_) {
      } finally {
        setIsAddingResults(false);
      }
    }
  };

  useMemo(() => {
    if (
      salesOrder?.diagnostic_results?.find((item) => item?.biomarker_text_result === 'Void') &&
      salesOrder.service.service_name.includes('PCR')
    ) {
      setShouldDisableCTValueForPCR(true);
    } else setShouldDisableCTValueForPCR(false);
  }, [salesOrder]);
  useEffect(() => {
    dispatch(fetchServicesTranslations());
  }, [dispatch]);

    const translatedServicesMap = useMemo(
    () => buildTranslatedServicesMap(servicesTranslations, lang),
    [servicesTranslations, lang]
  );
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      containerPadding="60px 20px 20px 20px"
      width={'97%'}
      maxWidth="900px"
    >
      <div className={classes.container}>
        <div className={classes.title}>{translations('add.results')}</div>
        <div className={classes.scanContainer}>
          <input
            type="text"
            className={classes.scanField}
            placeholder="New sample barcode"
            value={scanValue}
            onChange={onChangeScanValue}
          />
          {isEditingSample ? (
            <Spinner />
          ) : (
            <div
              className={classes.scanButton}
              style={{
                backgroundColor: scanValue.trim() === '' ? '#979797' : '#0b1b5c',
              }}
              onClick={() => {
                if (scanValue.trim() === '') return;
                dispatch(
                  editSampleTaken({
                    sampleBarcode: scanValue,
                    sampleTakenId: sampleTakenId,
                  })
                );
              }}
            >
              {translations('edit.barcode')}
            </div>
          )}
        </div>

        {!showConfirmVoidModal && (
          <div className={classes.patientTab}>
            <div className={classes.diagnosticHolder}>
              <div className={classes.diagnostic}>
                      {getTranslatedServicesName(
                    salesOrder?.service.service_id,
                    translatedServicesMap 
                  ) } 
              </div>
            </div>
            <div className={classes.employeeSelect}>
              <CustomDropdown
                label={translations('select.employee')}
                placeholder={translations('select.employee.validating')}
                onChange={(e: any) => setPersonIdSelected(e.target.value)}
                options={personsInLocation.map((person) => {
                  return {
                    id: person.person_id,
                    label: `${person.person_firstname} ${person.person_lastname}`,
                  };
                })}
                value={personIdSelected}
              />
            </div>
            <div className={classes.biomarkerTableContainer}>
              <div className={classes.biomarkerTableHeader}>
                <div className={classes.biomarkerTableHeaderItemSmall}>{translations('order')}</div>
                <div className={classes.biomarkerTableHeaderItemMedium}>
                  {translations('biomarkers')}
                </div>
                <div className={classes.biomarkerTableHeaderItemSmall}>
                  {translations('abbreviation')}
                </div>
                <div className={classes.biomarkerTableHeaderItemMedium}>
                  {translations('value')}
                </div>
                <div className={classes.biomarkerTableHeaderItemSmall}>{translations('UoM')}</div>
              </div>
              {salesOrder?.diagnostic_results
                ?.sort((a, b) => {
                  if (a.biomarker_order && b.biomarker_order) {
                    return a.biomarker_order < b.biomarker_order ? -1 : 1;
                  }
                  return 1;
                })
                .map((diagnosticResult) => {
                  const serviceWithBiomarker = getServiceBiomarker(
                    servicesWithBiomarkerInfo,
                    diagnosticResult.biomarker_id,
                    diagnosticResult.service_id
                  );
                  return (
                    <div key={diagnosticResult.biomarker_id} className={classes.biomarkerTableRow}>
                      <div className={classes.biomarkerValueSmall}>
                        {diagnosticResult.biomarker_order ?? ''}
                      </div>
                      <div className={classes.biomarkerValueMedium}>
                        {diagnosticResult.biomarker_name}
                      </div>
                      <div className={classes.biomarkerValueSmall}>
                        {diagnosticResult.biomarker_short_abbreviation
                          ? `${diagnosticResult.biomarker_short_abbreviation}`
                          : ''}
                      </div>
                      <div className={classes.biomarkerValueMedium}>
                        {diagnosticResult.biomarker_uom === 'Pos/Neg' ? (
                          <select
                            className={classes.biomarkerTableRowInputLarge}
                            value={diagnosticResult.biomarker_text_result ?? ''}
                            onChange={(e: any) => {
                              let tempoSelectedOrder: SalesOrderInfo = { ...salesOrder };
                              let a: DiagnosticResult[] = [];
                              tempoSelectedOrder.diagnostic_results?.forEach((d) => {
                                if (
                                  d.sample_taken_id === diagnosticResult.sample_taken_id &&
                                  d.biomarker_id === diagnosticResult.biomarker_id
                                ) {
                                  a.push({
                                    ...d,
                                    biomarker_text_result: e.target.value,
                                  });
                                } else {
                                  a.push({ ...d });
                                }
                              });
                              tempoSelectedOrder.diagnostic_results = [...a];
                              setSalesOrder(tempoSelectedOrder);
                            }}
                          >
                            <option disabled value="">
                              {translations('select.result')}
                            </option>
                            <option value="Positive">{translations('positive')}</option>
                            <option value="Negative">{translations('negative')}</option>
                            <option value="Void">{translations('void')}</option>
                          </select>
                        ) : (
                          <div
                            style={
                              shouldDisableCTValueForPCR
                                ? { pointerEvents: 'none', opacity: '0.3' }
                                : { pointerEvents: 'all', opacity: '1' }
                            }
                          >
                            <CustomTextField
                              value={diagnosticResult.biomarker_value_result?.toString() ?? ''}
                              height="50px"
                              maxWidth="185px"
                              type="number"
                              disabled={
                                salesOrder.serviceAdditionalInfo?.service_execution_category_id ==
                                  4 &&
                                !salesOrder.work_order_service_diagnostic?.result_delivery_time
                              }
                              onChange={(e: any) => {
                                let tempoSelectedOrder: SalesOrderInfo = { ...salesOrder };
                                let a: DiagnosticResult[] = [];
                                tempoSelectedOrder.diagnostic_results?.forEach((d) => {
                                  if (
                                    d.sample_taken_id === diagnosticResult.sample_taken_id &&
                                    d.biomarker_id === diagnosticResult.biomarker_id
                                  ) {
                                    a.push({
                                      ...d,
                                      biomarker_value_result: e.target.value,
                                    });
                                  } else {
                                    a.push({ ...d });
                                  }
                                });
                                tempoSelectedOrder.diagnostic_results = [...a];
                                setSalesOrder(tempoSelectedOrder);
                              }}
                              biomarkerValues={{
                                value1: serviceWithBiomarker?.minValue,
                                value2: serviceWithBiomarker?.maxValue,
                              }}
                              errorValidation={
                                !(
                                  serviceWithBiomarker?.minValue &&
                                  diagnosticResult?.biomarker_value_result &&
                                  serviceWithBiomarker.minValue <=
                                    +parseFloat(
                                      diagnosticResult.biomarker_value_result.toString()
                                    ).toFixed(2)
                                ) ||
                                !(
                                  serviceWithBiomarker?.maxValue &&
                                  diagnosticResult?.biomarker_value_result &&
                                  serviceWithBiomarker.maxValue >=
                                    +parseFloat(
                                      diagnosticResult.biomarker_value_result.toString()
                                    ).toFixed(2)
                                )
                              }
                            />
                          </div>
                        )}
                      </div>
                      <div className={classes.biomarkerValueSmall}>
                        <input
                          className={classes.biomarkerTableRowInputMedium}
                          value={diagnosticResult.biomarker_uom}
                          disabled={true}
                          onChange={() => {}}
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
        {showConfirmVoidModal && (
          <div>
            <div className={classes.voidConfirm}>{translations('void.confirm')}</div>
          </div>
        )}
        {isAddingResults ? (
          <Spinner />
        ) : showConfirmVoidModal ? (
          <div className={classes.voidButtons}>
            <PrimaryButtonV2
              text={translations('yes')}
              containerStyle={{
                width: '150px',
                marginTop: 20,
                marginRight: 30,
              }}
              buttonStyle={{
                height: 46,
                width: '100%',
                borderRadius: 8,
              }}
              onClick={markAsVoidHandler}
            />
            <PrimaryButtonV2
              text={translations('no')}
              containerStyle={{
                width: '150px',
                marginTop: 20,
              }}
              buttonStyle={{
                height: 46,
                width: '100%',
                borderRadius: 8,
              }}
              onClick={() => setShowConfirmVoidModal(false)}
              invertedColors={true}
            />
          </div>
        ) : (
          <div className={classes.buttonHolder}>
            <PrimaryButtonV2
              text={translations('add.result')}
              containerStyle={{
                width: '100%',
                marginTop: 20,
              }}
              buttonStyle={{
                height: 46,
                width: '100%',
                borderRadius: 8,
              }}
              disabled={!personIdSelected}
              onClick={() => addResultHandler()}
            />
            {!salesOrder?.service.service_name?.toLowerCase().includes('pcr') && (
              <PrimaryButtonV2
                text={translations('void')}
                containerStyle={{
                  width: '100%',
                  marginTop: 20,
                }}
                buttonStyle={{
                  height: 46,
                  width: '100%',
                  borderRadius: 8,
                }}
                onClick={() => setShowConfirmVoidModal(true)}
                invertedColors={true}
                disabled={!personIdSelected}
              />
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default EditSampleBarcodeModal;
